/**
 * SpotlightCard — mouse-tracking radial gradient spotlight.
 *
 * A card wrapper that renders a soft light effect following the
 * cursor. Works on dark backgrounds. Children render on top.
 *
 * Inspired by React Bits. Pure CSS + React mouse events (no deps).
 */

import { useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface SpotlightCardProps {
  children: ReactNode
  /** Color of the spotlight glow. Default: white at 15% opacity */
  spotlightColor?: string
  /** Size of the spotlight in px. Default: 300 */
  spotlightSize?: number
  className?: string
}

export function SpotlightCard({
  children,
  spotlightColor = 'rgba(255, 255, 255, 0.12)',
  spotlightSize = 300,
  className,
}: SpotlightCardProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const [isHovering, setIsHovering] = useState(false)

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: decorative cursor-tracking glow; not an interactive control
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className={cn('relative overflow-hidden', className)}
    >
      {/* Spotlight overlay */}
      <div
        className="pointer-events-none absolute inset-0 z-10 transition-opacity duration-300"
        style={{
          opacity: isHovering ? 1 : 0,
          background: `radial-gradient(${spotlightSize}px circle at ${pos.x}px ${pos.y}px, ${spotlightColor}, transparent 70%)`,
        }}
      />
      {/* Content */}
      <div className="relative z-20">{children}</div>
    </div>
  )
}
